import { useState } from "react";
import { ActionHandler, ChatListItem, EntryListItemDefault } from "@/components/UI/tools";
import { EmptyRow, Pill, Row, Section } from "@/app/workspace/settings/ui";
import { ArrowDown, ArrowRight, Remove } from "@/components/UI/svgs";
import { GallerySection, Placeholder, Specimen, noop } from "../galleryKit";

const SectionCardDemo = () => {
    const [selected, setSelected] = useState(1);
    return (
        <Section title="Section title" hint="Optional hint" action={<Pill tone="accent">3</Pill>}>
            {[0, 1, 2].map((i) => (
                <Row key={i} selected={selected === i} onClick={() => setSelected(i)} chevron trailing={<Pill>Label</Pill>}>
                    <p className="l2">Row {i + 1}</p>
                </Row>
            ))}
            <Row disabled onClick={noop}><p className="l2">Disabled row</p></Row>
            <Row><div className="flex flex-col gap-[.35rem]"><p className="l2">Row with detail</p><p className="detail-text">Secondary detail line</p></div></Row>
            <div className="row-form row-form-active">
                <Placeholder label="row-form" />
            </div>
            <div className="row-actions">
                <ActionHandler funq={noop} className="btn-ghost btn-sm">Cancel</ActionHandler>
                <ActionHandler funq={noop} className="btn-soft btn-sm">Save</ActionHandler>
            </div>
            <EmptyRow>Empty state row</EmptyRow>
        </Section>
    );
};

const PillsDemo = () => (
    <div className="flex flex-col gap-1">
        <div className="flex flex-row flex-wrap gap-[.35rem]">
            <Pill>Neutral</Pill>
            <Pill tone="accent">Accent</Pill>
            <Pill tone="warn">Warn</Pill>
            <Pill tone="danger">Danger</Pill>
            <Pill tone="good">Good</Pill>
            <Pill onClick={noop}>Interactive</Pill>
        </div>
        <div className="flex flex-row flex-wrap gap-[.35rem] h-[1.5rem]">
            <span className="chip">Chip</span>
            <span className="chip"><span className="chip-prefix">Kind&nbsp;</span>Chip</span>
            <span className="chip chip-added">Added</span>
            <span className="chip chip-removed">Removed</span>
        </div>
        <div className="tag-list">
            <span className="tag">research</span>
            <span className="tag">Agents_Gamma</span>
        </div>
        <div className="flex flex-row flex-wrap gap-[.35rem]">
            <code className="code-chip">param</code>
            <code className="code-chip code-chip-muted">unused</code>
            <code className="code-chip code-chip-good">present</code>
            <code className="code-chip code-chip-danger">missing</code>
        </div>
    </div>
);

const NavItemDemo = () => {
    const [selected, setSelected] = useState(0);
    return (
        <div className="relative flex flex-col gap-[.35rem] w-[11rem] pl-[6px]">
            {["Item one", "Item two", "Item three"].map((label, i) => (
                <ChatListItem key={label} isSelected={selected === i} isDisabled={false} onClick={() => setSelected(i)} hasRevisions={i === 1} hasChanges={i === 2}>
                    {label}
                </ChatListItem>
            ))}
            <ChatListItem isSelected={false} isDisabled isTransparent onClick={noop}>Disabled</ChatListItem>
            <ChatListItem isSelected={false} isDisabled={false} isTransparent onClick={noop}>Ghost</ChatListItem>
        </div>
    );
};

const EntryRowDemo = () => {
    const [plural, setPlural] = useState<number[]>([2]);
    const [singular, setSingular] = useState<number | undefined>();
    const idParams = { singular: { get: singular, set: setSingular }, plural: { get: plural, set: setPlural } };
    const items = [
        { id: 1, val: "Entry", lock: false },
        { id: 2, val: "Selected entry", lock: false },
        { id: 3, val: "Highlighted entry", lock: false, highlighted: true },
        { id: 4, val: "Locked entry", lock: true },
        { id: 5, val: "Show-only entry", lock: false, showonly: true },
    ];
    return (
        <div className="flex flex-col max-w-[28rem]">
            <div className="list-header">
                <small className="meta">Type</small>
                <small className="meta">-</small>
                <small className="meta-strong">Subtitle</small>
            </div>
            {items.map((item) => <EntryListItemDefault key={item.id} loading={false} item={item} idParams={idParams} />)}
        </div>
    );
};

const TreeDemo = () => {
    const [selected, setSelected] = useState("child-a");
    const nodes = [
        { id: "root", label: "Root", depth: 0, children: true },
        { id: "child-a", label: "Child A", depth: 1, children: false },
        { id: "child-b", label: "child.key.mono", depth: 1, children: false, mono: true },
        { id: "child-c", label: "Unregistered", depth: 1, children: false, muted: true },
    ];
    return (
        <div className="flex flex-col max-w-[22rem]">
            <input className="search mb-[.35rem]" placeholder="Filter…" />
            {nodes.map((n) => (
                <div
                    key={n.id}
                    className={`tree-node ${selected === n.id ? "tree-node-selected" : ""}`}
                    style={{ paddingLeft: n.depth * 14 }}
                    onClick={() => setSelected(n.id)}
                >
                    <span className={`tree-node-toggle ${n.children ? "" : "tree-node-toggle-empty"}`}><ArrowDown size={14} /></span>
                    <span className={`tree-node-label ${n.mono ? "tree-node-label-mono" : ""} ${n.muted ? "tree-node-label-muted" : ""}`}>{n.label}</span>
                    <span className="tree-node-action"><ArrowRight size={14} /></span>
                </div>
            ))}
            <p className="tree-empty">No matches</p>
            <button className="key-link mt-[.35rem] px-[.5rem]">jump.to.key</button>
        </div>
    );
};

const ListCardDemo = () => (
    <div className="flex flex-col gap-[.35rem] max-w-[28rem]">
        {["First item", "Second item"].map((t) => (
            <div key={t} className="list-card list-card-interactive reveal-host">
                <p className="b flex-grow">{t}</p>
                <span className="reveal icon-btn"><Remove size={16} /></span>
            </div>
        ))}
    </div>
);

const TableDemo = () => (
    <table className="data-table">
        <thead>
            <tr><th>Name</th><th>Kind</th><th>Status</th></tr>
        </thead>
        <tbody>
            <tr><td>row-one</td><td>alpha</td><td><Pill tone="good">active</Pill></td></tr>
            <tr><td>row-two</td><td>beta</td><td><Pill>idle</Pill></td></tr>
            <tr className="data-table-row-inactive"><td>row-three</td><td>gamma</td><td><Pill tone="danger">inactive</Pill></td></tr>
        </tbody>
    </table>
);
const MasterDetailDemo = () => {
    const [selected, setSelected] = useState(0);
    return (
        <div className="master-detail h-[12rem]">
            <div className="master-detail-list">
                {["item-one", "item-two", "item-three"].map((t, i) => (
                    <button key={t} onClick={() => setSelected(i)} className={`master-detail-item ${selected === i ? "master-detail-item-selected" : ""} ${i === 2 ? "master-detail-item-inactive" : ""}`}>
                        {t}
                    </button>
                ))}
            </div>
            <div className="master-detail-main"><Placeholder label="detail" className="h-full" /></div>
        </div>
    );
};

const AutocompleteDemo = () => (
    <div className="relative max-w-[22rem] h-[9rem]">
        <input className="input" defaultValue="Que" />
        <div className="autocomplete">
            {["Query one", "Query two", "Query three"].map((t) => <button key={t} className="autocomplete-option">{t}</button>)}
        </div>
    </div>
);

export const ListsSection = () => (
    <GallerySection id="lists" title="Lists and rows" intro="Repeated items: rows in cards, navigation items, entries, trees, tags and tables.">
        <Specimen
            name="Section, card and row"
            classes={["section", "section-header", "caption", "hint", "card", "row", "row-interactive", "row-selected", "row-disabled", "row-chevron", "row-empty", "row-form", "row-form-active", "row-actions", "row-actions-block", "detail-text", "detail-text-strong"]}
            usage="The settings building block: a caption, then one card holding rows. Rows separate themselves, so a card never counts its children. Use Section / Card / Row / EmptyRow from settings/ui."
            rules={["Put actions for the whole group in Section's action slot.", "leading / trailing slots hold icons and pills; keep row text to l2 or l."]}
            code={`<Section title="People" action={<Pill>3</Pill>}>
  <Row onClick={open} chevron trailing={<Pill tone="good">active</Pill>}>…</Row>
</Section>`}
            demo={() => <SectionCardDemo />}
        />
        <Specimen
            name="Pill, chip and code chip"
            classes={["pill", "pill-accent", "pill-warn", "pill-danger", "pill-good", "pill-interactive", "chip", "chip-prefix", "chip-added", "chip-removed", "tag-list", "tag", "code-chip", "code-chip-muted", "code-chip-good", "code-chip-danger"]}
            usage="pill: status or count. chip: a value inside a value box. tag: a read-only tag reference in a list row. code-chip: a monospace key or parameter."
            demo={() => <PillsDemo />}
        />
        <Specimen
            name="Nav item"
            classes={["nav-item", "nav-item-selected", "nav-item-ghost", "nav-item-disabled", "nav-item-flag-revisions", "nav-item-flag-changes"]}
            usage="Compact item in the chat side columns. Flags in the left gutter mark open revisions (blue) and unsaved changes (amber). Rendered by ChatListItem; its container must be relative."
            demo={() => <NavItemDemo />}
        />
        <Specimen
            name="Entry row"
            classes={["list-header", "meta", "meta-strong", "entry-row", "entry-row-highlighted", "entry-row-static", "entry-row-check", "entry-row-check-mark", "entry-row-check-mark-selectable", "entry-row-check-mark-selected", "entry-row-label"]}
            usage="Row in a CRUD list (EntryList). The left strip toggles multi-select; the label opens the entry."
            demo={() => <EntryRowDemo />}
        />
        <Specimen
            name="Tree node"
            classes={["tree-node", "tree-node-selected", "tree-node-toggle", "tree-node-toggle-empty", "tree-node-label", "tree-node-label-mono", "tree-node-label-muted", "tree-node-action", "tree-empty", "key-link"]}
            usage="Indented, selectable row for hierarchies (organisations, kvp and i18n keys). Indent with inline paddingLeft = depth × 14px; that is data, not style."
            demo={() => <TreeDemo />}
        />
        <Specimen
            name="List card with hover action"
            classes={["list-card", "list-card-interactive", "reveal-host", "reveal"]}
            usage="Bordered card per item when each item carries free text (preferences, overrides). reveal shows an action only while its reveal-host is hovered."
            demo={() => <ListCardDemo />}
        />
        <Specimen
            name="Table"
            classes={["data-table", "data-table-row-inactive"]}
            usage="Dense monospace table for admin data."
            demo={() => <TableDemo />}
        />
        <Specimen
            name="Master-detail"
            classes={["master-detail", "master-detail-list", "master-detail-item", "master-detail-item-selected", "master-detail-item-inactive", "master-detail-main"]}
            usage="List of records on the left, the selected record on the right. Used in the admin tools."
            demo={() => <MasterDetailDemo />}
        />
        <Specimen
            name="Autocomplete"
            classes={["autocomplete", "autocomplete-option"]}
            usage="Suggestions under an input. The parent must be relative."
            demo={() => <AutocompleteDemo />}
        />
        <Specimen
            name="Divider"
            classes={["divider", "menu-divider", "divider-label"]}
            usage="divider separates blocks; menu-divider separates groups inside a menu; divider-label is a rule with a centered word."
            demo={() => (
                <div className="flex flex-col gap-1 max-w-[22rem]">
                    <Placeholder />
                    <div className="divider" />
                    <Placeholder />
                    <div className="divider-label">or</div>
                </div>
            )}
        />
    </GallerySection>
);
